import { ProductVariant } from "@/types/product-type";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Order calculations
export type CalculatedTotals = {
  itemsSubtotal: number;
  discountAmount: number;
  deliveryFee: number;
  total: number;
};

export function calculateOrderTotals(params: {
  items: { unitPrice: number; quantity: number }[];
  discountPercentage: number;
  deliveryFee: number;
}): CalculatedTotals {
  const itemsSubtotal = params.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const discountAmount = itemsSubtotal * (Math.max(0, Math.min(100, params.discountPercentage)) / 100);
  const deliveryFee = Math.max(0, params.deliveryFee);
  const total = Math.max(0, itemsSubtotal - discountAmount + deliveryFee);
  return { itemsSubtotal, discountAmount, deliveryFee, total };
}

export function getFileExtension(url: string): string | null {
  try {
    // remove query and fragment
    const cleanUrl = url.split('?')[0].split('#')[0];
    const parts = cleanUrl.split('.');
    if (parts.length < 2) return null;
    return parts.pop()?.toLowerCase() ?? null;
  } catch {
    return null;
  }
}

/**
 * Checks if a URL (or file path) has an MP4 extension.
 */
export function isMp4File(url: string): boolean {
  const ext = getFileExtension(url);
  return ext === 'mp4';
}

// Date/time formatting
export function formatDateLabel(dateInput: string | number | Date): string {
  const date = new Date(dateInput);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatTimeLabel(dateInput: string | number | Date): string {
  const date = new Date(dateInput);
  return date.toLocaleTimeString();
}
export function formatShortDate(value?: string | null) {
  if (value === undefined || value === null) {
    return '-';
  }
  // value can be "2025-11-14" or ISO string
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}
export function getStockQuantity(variant: ProductVariant): number {
  if (typeof variant.stock === "object" && variant.stock !== null) {
    return variant.stock.closingQty ?? 0;
  }
  if (typeof variant.stock === "number") {
    return variant.stock;
  }
  return 0;
}
export function getFormatTime(updatedTime?: string | null) {
  if (!updatedTime) return '';
  const date = new Date(updatedTime);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

  // Fallback: show date (YYYY/MM/DD)
  return `${date.getFullYear()}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}`;
}
export enum CounterKey {
  INBOX = "INBOX_KEY",
  LIVE = "LIVE_KEY",
  POST = "POST_KEY",
  AUTO = "AUTO_KEY",
  QUEUE = "QUEUE_KEY",
}
export function incrementCounter(key: string) {
  var currentCounter = getCounter(key);
  currentCounter++;
  localStorage.setItem(key, `${currentCounter}`);

}
export function decrementCounter(key: string) {
  var currentCounter = getCounter(key);
  currentCounter--;
  if (currentCounter < 0) currentCounter = 0;
  localStorage.setItem(key, `${currentCounter}`);

}

export function clearCounter(key: string) {
  localStorage.removeItem(key);
}
export function getCounter(key: string): number {
  const result = localStorage.getItem(key);
  if (result) {
    return parseInt(result);
  }
  return 0;
}

/**
 * Get product limit based on device screen width
 * @param width - Window inner width
 * @returns Product limit: 6 for mobile, 20 for tablet, 24 for desktop
 */
export function getProductLimitByDevice(width: number): number {
  if (width <= 767) return 6; // Mobile: 6 items
  if (width <= 1023) return 20; // Tablet: 20 items
  return 24; // Desktop: 24 items (6 rows × 4 cols)
}
